import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  ShieldCheck,
  Download,
  ExternalLink,
  Search,
  CheckCircle,
  Database,
  KeyRound,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  FileText,
  Layers,
  Info,
  Lock,
  RefreshCw
} from "lucide-react";
import { alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import "./proof-reserves.css";

const AUDIT_SNAPSHOTS = [
  { id: "snap-2026-06", label: "June 2026 Audit (Latest)" },
  { id: "snap-2026-05", label: "May 2026 Audit" },
  { id: "snap-2026-04", label: "April 2026 Audit" }
];

const POR_ASSET_BREAKDOWN = [
  {
    coin: "USDT",
    fullName: "Tether USD",
    icon: "/images/staking_img/usdt_icon.svg",
    ratio: 105.78,
    liabilities: "124,560,780",
    assets: "131,760,393",
    status: "Verified 1:1"
  },
  {
    coin: "BTC",
    fullName: "Bitcoin",
    icon: "/images/staking_img/btc_icon.svg",
    ratio: 102.35,
    liabilities: "12,450.45",
    assets: "12,743.04",
    status: "Verified 1:1"
  },
  {
    coin: "ETH",
    fullName: "Ethereum",
    icon: "/images/staking_img/eth_icon.svg",
    ratio: 104.12,
    liabilities: "98,245.80",
    assets: "102,293.52",
    status: "Verified 1:1"
  },
  {
    coin: "SOL",
    fullName: "Solana",
    icon: "/images/staking_img/sol_icon.svg",
    ratio: 103.50,
    liabilities: "354,800.00",
    assets: "367,218.00",
    status: "Verified 1:1"
  },
  {
    coin: "GT",
    fullName: "AGCX Token",
    icon: "/images/staking_img/agce_icon.svg",
    ratio: 115.42,
    liabilities: "4,850,000.00",
    assets: "5,597,870.00",
    status: "Verified 1:1"
  },
  {
    coin: "USDC",
    fullName: "USD Coin",
    icon: "/images/staking_img/usdt_icon.svg",
    ratio: 102.10,
    liabilities: "45,210,000",
    assets: "46,159,410",
    status: "Verified 1:1"
  }
];

const POR_FAQ = [
  {
    q: "What is Proof of Reserves (PoR)?",
    a: "Proof of Reserves is AGCX’s custody check that our on-chain holdings cover user balances. In simple terms, it supports the claim that if clients withdrew at once, AGCX would hold matching reserves for those liabilities."
  },
  {
    q: "How does a Merkle Tree verification work?",
    a: "A Merkle Tree is a cryptographic structure where each leaf node represents an individual account's balance and hashed identifier. By hashing adjacent leaves together recursively up the tree, we arrive at a single 'Merkle Root' hash. By providing you with your account's hash and the necessary sister-branch hashes (Merkle Path), you can verify that your balance is cryptographically included in the final verified root, without exposing any other user's balance details."
  },
  {
    q: "What are Zero-Knowledge Proofs (zk-SNARKs) in PoR?",
    a: "Zero-Knowledge SNARKs let AGCX prove that user balances in the Merkle Tree are consistent with reserve holdings, without exposing individual user balances or identities."
  },
  {
    q: "How often are Proof of Reserves audits conducted?",
    a: "AGCX publishes Proof of Reserves snapshots on a regular schedule. Check the AGCX Proof of Reserves page for the latest snapshot and how to verify your inclusion."
  },
  {
    q: "Can the exchange cheat by including fake assets?",
    a: "AGCX must prove control of reserve wallets (for example by signing with the corresponding keys). On-chain assets are compared with total user liabilities in the Merkle tree. Fabricating those assets is not compatible with this verification model."
  }
];

const ProofOfReserves = () => {
  const [snapshot, setSnapshot] = useState("snap-2026-06");
  const [userId, setUserId] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedResult, setVerifiedResult] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [activeTab, setActiveTab] = useState("quick-check"); // quick-check | tech-details
  const [assetSearch, setAssetSearch] = useState("");

  const handleVerify = (e) => {
    e.preventDefault();
    if (!userId.trim()) return;

    setIsVerifying(true);
    setVerifiedResult(null);

    setTimeout(() => {
      setIsVerifying(false);

      const userHash = "0x" + Math.abs(hashCode(userId + verifyCode)).toString(16).padEnd(12, "f") + "a1c3";
      const leafL1Right = "0x2d8f9b" + Math.abs(hashCode(userId + "l1")).toString(16).substring(0, 6) + "f7a1";
      const branchL2Left = "0x3a5fd1" + Math.abs(hashCode(userId + "l2")).toString(16).substring(0, 6) + "d10e";
      const rootHash = "0x8c7b4e" + Math.abs(hashCode("merkle-root-por-2026")).toString(16).substring(0, 6) + "4e9a";

      setVerifiedResult({
        userId: userId,
        snapshotDate: AUDIT_SNAPSHOTS.find(s => s.id === snapshot)?.label,
        balances: [
          { asset: "BTC", amount: "0.24580000" },
          { asset: "ETH", amount: "1.85000000" },
          { asset: "USDT", amount: "1,250.00" }
        ],
        hashes: {
          userLeaf: userHash,
          sisterNode: leafL1Right,
          parentBranch: branchL2Left,
          root: rootHash
        }
      });
      alertSuccessMessage("Verification completed successfully!");
    }, 1200);
  };

  const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
  };

  const handleDownload = (fileName) => {
    alertSuccessMessage(`Downloaded file: ${fileName}`);
    const dataStr = JSON.stringify({
      exchange: "ARAB Global",
      snapshot: snapshot,
      timestamp: new Date().toISOString(),
      merkle_root: "0x8c7b4e54911d102e3a5fd10ebd7472097b2ca89f",
      verification_status: "Verified 100% Solvent"
    }, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', fileName);
    linkElement.click();
  };

  // Filtered assets for the table
  const filteredAssets = POR_ASSET_BREAKDOWN.filter(asset =>
    asset.coin.toLowerCase().includes(assetSearch.toLowerCase()) ||
    asset.fullName.toLowerCase().includes(assetSearch.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>Proof of Reserves Solvency Dashboard – ARAB Global</title>
        <meta
          name="description"
          content="Access the fully transparent Proof of Reserves console. Audit your own balances, inspect the Merkle tree paths, and download financial files."
        />
      </Helmet>

      <div className="por-page-container top-section-gap">
        {/* Decorative Grid Patterns & Ambient Light */}
        <div className="por-mesh-grid"></div>
        <div className="por-radial-spot por-spot-indigo"></div>
        <div className="por-radial-spot por-spot-amber"></div>

        <div className="container py-5">

          {/* Main Bento Grid */}
          <div className="por-bento-grid">

            {/* Bento Card 1: Main Page Header & Philosophy (Spans 2 columns) */}
            <div className="por-bento-card card-hero">
              <div className="d-flex flex-column h-100 justify-content-between">
                <div>
                  {/* <div className="d-inline-flex align-items-center gap-2 por-status-badge mb-3">
                    <span className="pulse-dot"></span>
                    <span className="por-badge-text">Audited & Fully solvent</span>
                  </div> */}
                  <h1 className="por-main-title text-gradient">Cryptographic Solvency & Reserves</h1>
                  <p className="por-subtitle">
                    Unlike standard financial systems, we prove our solvency cryptographically.
                    Every single deposit is backed 1-to-1 in our secure vault wallets.
                    We don't ask for your trust—we deliver verifiable proof.
                  </p>
                </div>
                <div className="por-hero-features-row mt-4 d-flex gap-4 flex-wrap">
                  <div className="por-feat-badge">
                    <ShieldCheck size={16} /> 100% Backed Liabilities
                  </div>
                  <div className="por-feat-badge">
                    <Layers size={16} /> Merkle Tree Inclusion Proofs
                  </div>
                  <div className="por-feat-badge">
                    <Lock size={16} /> zk-SNARK Privacy Shields
                  </div>
                </div>
              </div>
            </div>

            {/* Bento Card 2: Dynamic Collateral Circle Gauge (Spans 1 column) */}
            <div className="por-bento-card card-gauge text-center d-flex flex-column justify-content-center align-items-center">
              <div className="por-svg-gauge-container">
                <svg className="por-circle-gauge" viewBox="0 0 120 120">
                  <circle className="gauge-bg-circle" cx="60" cy="60" r="50"></circle>
                  <circle
                    className="gauge-val-circle"
                    cx="60"
                    cy="60"
                    r="50"
                    style={{ strokeDashoffset: `${314 - (314 * 104.85) / 120}` }}
                  ></circle>
                </svg>
                <div className="por-gauge-text-overlay">
                  <span className="ratio-pct">104.85%</span>
                  <span className="ratio-lbl">Collateralized</span>
                </div>
              </div>
              <h4 className="text-white mt-4 mb-2 font-outfit">Exchange Reserves Ratio</h4>
              <p className="text-muted small px-3">
                Consolidated reserves match or exceed all user deposit liabilities across the platform.
              </p>
              <div className="por-gauge-mini-stats mt-3 w-100 d-flex justify-content-around">
                <div>
                  <span className="stat-lbl d-block small text-muted">Liabilities</span>
                  <span className="stat-val font-monospace text-white fw-bold">$452.8M</span>
                </div>
                <div className="border-start border-secondary opacity-50"></div>
                <div>
                  <span className="stat-lbl d-block small text-muted">Reserves</span>
                  <span className="stat-val font-monospace text-white fw-bold">$474.8M</span>
                </div>
              </div>
            </div>

            {/* Bento Card 3: Self-Verification Console (Spans 2 columns) */}
            <div className="por-bento-card card-verify-portal">
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                  <h3 className="por-panel-title text-white mb-1">Verify Your Inclusion</h3>
                  <p className="text-muted small mb-0">Compute your account's leaf placement in the verified root</p>
                </div>
                {/* Mode Tabs */}
                <div className="por-btn-group-pill">
                  <button
                    className={`pill-tab-btn ${activeTab === "quick-check" ? "active" : ""}`}
                    onClick={() => setActiveTab("quick-check")}
                  >
                    Quick Check
                  </button>
                  <button
                    className={`pill-tab-btn ${activeTab === "tech-details" ? "active" : ""}`}
                    onClick={() => setActiveTab("tech-details")}
                  >
                    Audit Details
                  </button>
                </div>
              </div>

              {activeTab === "quick-check" ? (
                <div className="row g-4">
                  <div className="col-lg-5">
                    <form onSubmit={handleVerify} className="por-verify-form">
                      <div className="mb-3">
                        <label className="form-label text-muted small">Audit Snapshot</label>
                        <select
                          className="form-select por-form-input"
                          value={snapshot}
                          onChange={(e) => setSnapshot(e.target.value)}
                        >
                          {AUDIT_SNAPSHOTS.map(snap => (
                            <option key={snap.id} value={snap.id}>{snap.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="form-label text-muted small">Account ID / Username</label>
                        <div className="input-group">
                          <span className="input-group-text por-input-addon"><Search size={14} /></span>
                          <input
                            type="text"
                            className="form-control por-form-input"
                            placeholder="Enter Username"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="form-label text-muted small">Audit Verification Salt</label>
                        <input
                          type="text"
                          className="form-control por-form-input"
                          placeholder="Optional code"
                          value={verifyCode}
                          onChange={(e) => setVerifyCode(e.target.value)}
                        />
                      </div>

                      <button
                        type="submit"
                        className="btn por-verify-btn w-100 py-3"
                        disabled={isVerifying}
                      >
                        {isVerifying ? (
                          <span className="d-flex align-items-center justify-content-center gap-2">
                            <RefreshCw className="animate-spin" size={16} /> Hashing...
                          </span>
                        ) : (
                          "Check Inclusion Proof"
                        )}
                      </button>
                    </form>
                  </div>

                  <div className="col-lg-7">
                    {verifiedResult ? (
                      <div className="por-interactive-merkle-flow h-100 p-3 rounded">
                        <div className="d-flex align-items-center gap-2 text-success mb-3">
                          <CheckCircle size={20} />
                          <span className="fw-bold text-white">Inclusion Verified in Merkle Tree!</span>
                        </div>

                        <div className="por-tree-flow">
                          <div className="por-tree-node root">
                            <span className="node-badge root-badge">Root Hash</span>
                            <div className="node-hash mt-1">{verifiedResult.hashes.root}</div>
                          </div>

                          <div className="por-tree-connector"></div>

                          <div className="por-tree-node branch">
                            <span className="node-badge branch-badge">Level 1 Parent Node</span>
                            <div className="node-hash mt-1">{verifiedResult.hashes.parentBranch}</div>
                          </div>

                          <div className="por-tree-connector"></div>

                          <div className="d-flex gap-2 w-100 justify-content-center">
                            <div className="por-tree-node leaf active flex-fill">
                              <span className="node-badge leaf-badge">Your Account Leaf</span>
                              <div className="node-hash mt-1">{verifiedResult.hashes.userLeaf}</div>
                            </div>
                            <div className="por-tree-node leaf sibling flex-fill">
                              <span className="node-badge sibling-badge">Sibling Hash</span>
                              <div className="node-hash mt-1">{verifiedResult.hashes.sisterNode}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="por-verifier-empty-state d-flex flex-column align-items-center justify-content-center h-100 text-center p-4">
                        <KeyRound size={48} className="text-indigo opacity-30 mb-3" />
                        <h5 className="text-white">Awaiting Input Credentials</h5>
                        <p className="text-muted small max-w-280">
                          Enter your account credentials to compute and build the validation node graph.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="por-tech-info p-3">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="d-flex gap-3 align-items-start">
                        <div className="tech-icon-wrap"><Layers size={20} /></div>
                        <div>
                          <h5 className="text-white">zk-SNARK Constraints</h5>
                          <p className="text-muted small">
                            Our monthly audits use Zero-Knowledge Succinct Non-Interactive Arguments of Knowledge.
                            This guarantees that our system doesn't hide any negative balance accounts to fake reserves.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex gap-3 align-items-start">
                        <div className="tech-icon-wrap"><Database size={20} /></div>
                        <div>
                          <h5 className="text-white">On-chain Proof of Keys</h5>
                          <p className="text-muted small">
                            We prove ownership of cold and hot wallets by executing cryptographic signature challenges periodically.
                            The matching assets on-chain are compared with total database liabilities.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="por-warning-banner mt-4 p-3 rounded d-flex gap-3 align-items-center">
                    <Info className="text-amber" size={20} />
                    <span className="small text-muted">
                      Hacken independently certifies our mathematical models monthly.
                      You can download raw tree files below to run locally.
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Bento Card 4: Downloads & Quick Links (Spans 1 column) */}
            <div className="por-bento-card card-downloads d-flex flex-column justify-content-between">
              <div>
                <h4 className="text-white mb-2 font-outfit">Download Station</h4>
                <p className="text-muted small mb-4">
                  Retrieve raw files to run local audits on your terminal.
                </p>
              </div>

              <div className="d-flex flex-column gap-3">
                <button
                  onClick={() => handleDownload("merkle_tree.json")}
                  className="btn por-bento-download-btn d-flex align-items-center justify-content-between"
                >
                  <span className="btn-inner">
                    <FileText size={16} />
                    <span>Merkle Tree Structure</span>
                  </span>
                  <Download size={14} />
                </button>

                <button
                  onClick={() => handleDownload("user_config.json")}
                  className="btn por-bento-download-btn d-flex align-items-center justify-content-between"
                >
                  <span className="btn-inner">
                    <KeyRound size={16} />
                    <span>User Leaf Config</span>
                  </span>
                  <Download size={14} />
                </button>

                <a
                  href="https://github.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn por-bento-download-btn d-flex align-items-center justify-content-between"
                >
                  <span className="btn-inner">
                    <ExternalLink size={16} />
                    <span>zk-SNARK Verifier (Git)</span>
                  </span>
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>

            {/* Bento Card 5: Asset-by-Asset Reserves Grid Table (Spans 3 columns) */}
            <div className="por-bento-card card-table card-full">
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                  <h3 className="por-panel-title text-white mb-1">Asset-by-Asset Reserve Breakdown</h3>
                  <p className="text-muted small mb-0">Check individual collateral ratios. All key assets are kept over 100% backed.</p>
                </div>

                {/* Search Bar inside bento grid */}
                <div className="por-table-search-box">
                  <Search size={14} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search Asset..."
                    value={assetSearch}
                    onChange={(e) => setAssetSearch(e.target.value)}
                  />
                </div>
              </div>

              {/* Desktop Table view */}
              <div className="por-desktop-table-wrapper">
                <div className="table-responsive">
                  <table className="table por-bento-table align-middle">
                    <thead>
                      <tr>
                        <th>Asset</th>
                        <th>Reserves Ratio</th>
                        <th>Total Liabilities (User Deposits)</th>
                        <th>Total Assets (Exchange Holdings)</th>
                        <th className="text-end font-outfit">Audit Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAssets.length > 0 ? (
                        filteredAssets.map((asset) => (
                          <tr key={asset.coin}>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <img src={asset.icon} alt={asset.coin} className="por-table-coin-icon" />
                                <div>
                                  <span className="fw-bold text-white d-block">{asset.coin}</span>
                                  <span className="small text-muted">{asset.fullName}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <span className="fw-bold text-success font-monospace">{asset.ratio}%</span>
                                <div className="progress por-table-progress flex-grow-1" style={{ height: "4px" }}>
                                  <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{ width: `${Math.min(100, (asset.ratio / 120) * 100)}%` }}
                                    aria-valuenow={asset.ratio}
                                    aria-valuemin="0"
                                    aria-valuemax="120"
                                  ></div>
                                </div>
                              </div>
                            </td>
                            <td className="font-monospace text-white">{asset.liabilities} {asset.coin}</td>
                            <td className="font-monospace text-white">{asset.assets} {asset.coin}</td>
                            <td className="text-end">
                              <span className="badge por-status-indicator">{asset.status}</span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-muted">
                            No assets match "{assetSearch}"
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Cards view */}
              <div className="por-mobile-cards-wrapper">
                {filteredAssets.length > 0 ? (
                  filteredAssets.map((asset) => (
                    <div key={asset.coin} className="por-mobile-asset-card mb-3 p-3">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-3">
                          <img src={asset.icon} alt={asset.coin} className="por-table-coin-icon" />
                          <div>
                            <span className="fw-bold text-white d-block coin-name">{asset.coin}</span>
                            <span className="small text-muted">{asset.fullName}</span>
                          </div>
                        </div>
                        <span className="badge por-status-indicator">{asset.status}</span>
                      </div>

                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="small text-muted">Reserves Ratio</span>
                          <span className="fw-bold text-success font-monospace">{asset.ratio}%</span>
                        </div>
                        <div className="progress por-table-progress" style={{ height: "4px" }}>
                          <div
                            className="progress-bar bg-success"
                            role="progressbar"
                            style={{ width: `${Math.min(100, (asset.ratio / 120) * 100)}%` }}
                            aria-valuenow={asset.ratio}
                            aria-valuemin="0"
                            aria-valuemax="120"
                          ></div>
                        </div>
                      </div>

                      <div className="row g-2 pt-2 card-details-row">
                        <div className="col-6">
                          <span className="stat-lbl d-block small text-muted">Liabilities (User Deposits)</span>
                          <span className="stat-val font-monospace text-white small">{asset.liabilities} {asset.coin}</span>
                        </div>
                        <div className="col-6">
                          <span className="stat-lbl d-block small text-muted">Assets (Exchange Holdings)</span>
                          <span className="stat-val font-monospace text-white small">{asset.assets} {asset.coin}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-muted">
                    No assets match "{assetSearch}"
                  </div>
                )}
              </div>
            </div>

            {/* Bento Card 6: Educational FAQs (Spans 3 columns) */}
            <div className="por-bento-card card-faq card-full">
              <h3 className="por-panel-title text-white text-center mb-4">Frequently Asked Questions</h3>
              <div className="por-bento-faq-list">
                {POR_FAQ.map((faq, idx) => {
                  const isOpen = openFaq === idx;
                  return (
                    <div key={idx} className="por-faq-item-bento border-bottom border-secondary py-3">
                      <button
                        type="button"
                        className="w-100 btn d-flex justify-content-between align-items-center text-start p-0 shadow-none border-0"
                        onClick={() => setOpenFaq(isOpen ? null : idx)}
                      >
                        <span className="text-white fw-bold d-flex align-items-center gap-2 question-text">
                          <HelpCircle size={16} className="text-primary" /> {faq.q}
                        </span>
                        {isOpen ? <ChevronUp size={16} className="text-muted" /> : <ChevronDown size={16} className="text-muted" />}
                      </button>
                      {isOpen && (
                        <div className="mt-3 text-muted small px-4 lh-lg answer-text">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

          </div>

        </div>
      </div>
    </>
  );
};

export default ProofOfReserves;
